import type { RootState } from 'common/redux'
import { SyncPolicy } from './replication/sync-policy'

/**
 * How each slice of the live-session store reaches the presentation window
 * (rules in `replication/sync-policy.ts`). A new reducer in `common/redux.ts`
 * without a line here does not compile.
 */
export const ROOT_SYNC_POLICY: SyncPolicy<RootState> = {
	workflow: { kind: 'shared' },
	interactionScheme: { kind: 'shared' },
	roster: { kind: 'shared' },
	groupRoom: { kind: 'shared' },
	simpleGrouping: { kind: 'shared' },
	sessionInfo: { kind: 'shared' },
	sessionMode: { kind: 'shared' },
	questions: { kind: 'shared' },
	screenShare: { kind: 'shared' },
	whiteboards: { kind: 'shared' },
	notepads: { kind: 'shared' },
	wordCloud: { kind: 'shared' },
	multipleChoicePolling: { kind: 'shared' },
	listPolling: { kind: 'shared' },
	knowledgeChecks: { kind: 'shared' },
	groupEngagement: { kind: 'shared' },
	script: { kind: 'shared' },
	paramToggler: { kind: 'shared' },
	// The console's connection health: the presentation window has none.
	connectivity: { kind: 'shared' },

	// Each window has its own misc panel and stream position.
	room: { kind: 'partial', omit: ['miscPanel', 'draggableStreamPosition'] },
	lobby: { kind: 'partial', omit: ['editingProfile'] },
	// Dispatched during render in four components: never worth a message.
	organization: { kind: 'partial', omit: ['expandableCompanyCard'] },
	// A class instance with methods, and online grouping only.
	trainingRoom: { kind: 'partial', omit: ['groupsSuggestionAssignment'] },
	// The slide list is fetched by whichever window opens the switcher.
	slides: { kind: 'partial', omit: ['slides', 'loadingAll'] },
	// Which event stream this window has open - the presentation window none.
	eventSource: { kind: 'partial', omit: ['eventsSourcePath'] },

	// Both windows may fetch profiles; keep every one either has.
	users: {
		kind: 'merge',
		merge: (local, host) => ({ ...host, all: { ...local.all, ...host.all } }),
	},

	// The players live in the presentation window, and their offset
	// hand-shake branches on this window's own `meRequesting`: feed it the
	// server's events, not the console's conclusions.
	youtubeStreaming: { kind: 'relay', actionPrefixes: ['SSE_YOUTUBE_'] },
	audioStreaming: { kind: 'relay', actionPrefixes: ['SSE_AUDIO_'] },

	// Media streams cannot be cloned, and nothing in person has any.
	videoConferenceMedia: { kind: 'local' },
	// This window's own login.
	session: { kind: 'local' },

	// Black screen and speakers, set by the console.
	presenter: { kind: 'shared' },
}
